import * as XLSX from "xlsx";

/**
 * Format helper for currency
 */
const fmtEuro = (val) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(
    val,
  );

/**
 * Formats Quality Motor string for the table
 */
const fmtQuality = (agent) =>
  `0G: ${agent.count0Gar}, 1G: ${agent.count1Gar}, 2+G: ${agent.count2PlusGar}`;

/**
 * Logic to calculate missing policies for retention targets
 */
const calculateMissingPolicies = (agent, rawData, targetPerc) => {
  const totalExpiries = rawData.expiries.filter(
    (e) => e.collaboratore === agent.name,
  ).length;
  if (totalExpiries === 0) return 0;
  const targetCount = Math.ceil((targetPerc / 100) * totalExpiries);
  const currentCount = Math.round((agent.retentionRate / 100) * totalExpiries);
  return Math.max(0, targetCount - currentCount);
};

/**
 * Generates an Excel file with 2 sheets: KPI_Summary and Target_Mancanti.
 */
export const exportAgentToExcel = async (
  agent,
  rawData,
  aiInsights,
  includeAI,
) => {
  // Sheet 1: KPI_Summary
  const summaryRow = {
    Collaboratore: agent.name,
    Provvigioni: agent.baseCommission,
    "BONUS SCONTO": agent.totalBonusSconto,
    "Netto Motor": agent.nettoMotor,
    "# Polizze (N+R)": agent.polizzeStrict,
    "Qualità (Motor)*": fmtQuality(agent),
    "Media Gar.": agent.avgGarMotorTotal.toFixed(2),
    "Mix No-M": `${agent.mixNonMotor.toFixed(2)}%`,
    "Retention %": `${agent.retentionRate.toFixed(2)}%`,
  };

  // Sheet 2: Target_Mancanti
  const missingPoliciesBase = calculateMissingPolicies(agent, rawData, 70);
  const missingPoliciesSuper = calculateMissingPolicies(agent, rawData, 80);

  const targets = [
    {
      kpi: "Mix No-Motor",
      target: "1.3%",
      actual: `${agent.mixNonMotor.toFixed(2)}%`,
      ok: agent.reqCross,
      msg: agent.reqCross
        ? "Target OK"
        : `Manca lo ${(1.3 - agent.mixNonMotor).toFixed(2)}%`,
    },
    {
      kpi: "Garanzie Nuove",
      target: "2.0",
      actual: agent.avgGarMotorNew.toFixed(2),
      ok: agent.avgGarMotorNew >= 2.0,
      msg:
        agent.avgGarMotorNew >= 2.0
          ? "Target OK"
          : `Mancano ${(2.0 - agent.avgGarMotorNew).toFixed(2)}`,
    },
    {
      kpi: "Garanzie Rinnovo",
      target: "1.1",
      actual: agent.avgGarMotorRen.toFixed(2),
      ok: agent.avgGarMotorRen >= 1.1,
      msg:
        agent.avgGarMotorRen >= 1.1
          ? "Target OK"
          : `Mancano ${(1.1 - agent.avgGarMotorRen).toFixed(2)}`,
    },
    {
      kpi: "Ritenzione Base",
      target: "70%",
      actual: `${agent.retentionRate.toFixed(2)}%`,
      ok: agent.reqRetBase,
      msg: agent.reqRetBase
        ? "Target OK"
        : `Recupera ${missingPoliciesBase} polizze`,
    },
    {
      kpi: "Ritenzione Super",
      target: "80%",
      actual: `${agent.retentionRate.toFixed(2)}%`,
      ok: agent.reqRetSuper,
      msg: agent.reqRetSuper
        ? "Target OK"
        : `Mancano ${missingPoliciesSuper} per il Super Bonus`,
    },
  ];

  const wsSummary = XLSX.utils.json_to_sheet([summaryRow]);
  const wsTargets = XLSX.utils.json_to_sheet(
    targets.map((t) => ({
      KPI: t.kpi,
      Target: t.target,
      Attuale: t.actual,
      Esito: t.ok ? "Target OK" : "Mancanza",
      Messaggio: t.msg,
    })),
  );

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsSummary, "KPI_Summary");
  XLSX.utils.book_append_sheet(wb, wsTargets, "Target_Mancanti");

  XLSX.writeFile(wb, `Report_KPI_${agent.name.replace(/\s+/g, "_")}.xlsx`);
};

/**
 * Triggers a clean PDF print view with fixed column order and Target Mancanti section.
 */
export const printAgentPDF = (agent, rawData, aiInsights, includeAI) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const missingPoliciesBase = calculateMissingPolicies(agent, rawData, 70);
  const missingPoliciesSuper = calculateMissingPolicies(agent, rawData, 80);

  const html = `
    <html>
      <head>
        <title>Report KPI - ${agent.name}</title>
        <style>
          body { font-family: 'Inter', system-ui, sans-serif; color: #0f172a; padding: 30px; line-height: 1.4; }
          .header { border-bottom: 2px solid #e2e8f0; padding-bottom: 15px; margin-bottom: 25px; }
          .header h1 { margin: 0; font-size: 24px; font-weight: 900; text-transform: uppercase; color: #f97316; }
          .summary-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 11px; }
          .summary-table th { background: #f8fafc; color: #64748b; font-weight: 900; text-transform: uppercase; padding: 10px; border: 1px solid #e2e8f0; text-align: center; }
          .summary-table td { padding: 10px; border: 1px solid #e2e8f0; text-align: center; font-weight: 600; }
          .section-title { font-size: 14px; font-weight: 900; text-transform: uppercase; margin-bottom: 15px; color: #1e293b; border-bottom: 2px solid #f97316; display: inline-block; }
          .target-grid { display: grid; grid-template-cols: repeat(auto-fill, minmax(200px, 1fr)); gap: 15px; }
          .target-block { border: 1px solid #e2e8f0; padding: 12px; border-radius: 8px; background: #fff; }
          .target-name { font-size: 10px; font-weight: 900; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
          .target-status { font-size: 12px; font-weight: 800; margin-bottom: 2px; }
          .status-ok { color: #10b981; }
          .status-ko { color: #ef4444; }
          .target-val { font-size: 11px; font-weight: 600; color: #94a3b8; }
          .footer { margin-top: 40px; font-size: 9px; color: #94a3b8; font-weight: bold; border-top: 1px solid #f1f5f9; padding-top: 10px; }
          .ai-box { background: #fff7ed; border: 1px solid #ffedd5; padding: 15px; border-radius: 10px; font-size: 11px; margin-bottom: 25px; white-space: pre-wrap; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>MOBILITY <span style="color:#1e293b">MERITX</span></h1>
          <p style="font-size: 10px; font-weight: 900; color: #64748b; margin: 4px 0 0 0; text-transform: uppercase; letter-spacing: 1px;">Incentive Tracking & Performance Intelligence</p>
        </div>

        <div class="section-title">KPI Summary</div>
        <table class="summary-table">
          <thead>
            <tr>
              <th>Provvigioni</th>
              <th>BONUS SCONTO</th>
              <th>Netto Motor</th>
              <th># Polizze (N+R)</th>
              <th>Qualità (Motor)*</th>
              <th>Media Gar.</th>
              <th>Mix No-M</th>
              <th>Retention %</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${fmtEuro(agent.baseCommission)}</td>
              <td>${fmtEuro(agent.totalBonusSconto)}</td>
              <td>${fmtEuro(agent.nettoMotor)}</td>
              <td>${agent.polizzeStrict}</td>
              <td>${fmtQuality(agent)}</td>
              <td>${agent.avgGarMotorTotal.toFixed(1)}</td>
              <td>${agent.mixNonMotor.toFixed(1)}%</td>
              <td>${agent.retentionRate.toFixed(1)}%</td>
            </tr>
          </tbody>
        </table>

        <div class="section-title">Target Mancanti</div>
        <div class="target-grid">
          <div class="target-block">
            <div class="target-name">Mix No-Motor (1.3%)</div>
            <div class="target-status ${
              agent.reqCross ? "status-ok" : "status-ko"
            }">${
    agent.reqCross
      ? "Target OK"
      : `Manca lo ${(1.3 - agent.mixNonMotor).toFixed(1)}%`
  }</div>
            <div class="target-val">${agent.mixNonMotor.toFixed(2)}%</div>
          </div>
          <div class="target-block">
            <div class="target-name">Garanzie Nuove (2.0)</div>
            <div class="target-status ${
              agent.avgGarMotorNew >= 2.0 ? "status-ok" : "status-ko"
            }">${
    agent.avgGarMotorNew >= 2.0
      ? "Target OK"
      : `Mancano ${(2.0 - agent.avgGarMotorNew).toFixed(1)}`
  }</div>
            <div class="target-val">${agent.avgGarMotorNew.toFixed(2)}</div>
          </div>
          <div class="target-block">
            <div class="target-name">Garanzie Rinnovo (1.1)</div>
            <div class="target-status ${
              agent.avgGarMotorRen >= 1.1 ? "status-ok" : "status-ko"
            }">${
    agent.avgGarMotorRen >= 1.1
      ? "Target OK"
      : `Mancano ${(1.1 - agent.avgGarMotorRen).toFixed(1)}`
  }</div>
            <div class="target-val">${agent.avgGarMotorRen.toFixed(2)}</div>
          </div>
          <div class="target-block">
            <div class="target-name">Ritenzione Base (70%)</div>
            <div class="target-status ${
              agent.reqRetBase ? "status-ok" : "status-ko"
            }">${
    agent.reqRetBase ? "Target OK" : `Recupera ${missingPoliciesBase} polizze`
  }</div>
            <div class="target-val">${agent.retentionRate.toFixed(1)}%</div>
          </div>
          <div class="target-block">
            <div class="target-name">Ritenzione Super (80%)</div>
            <div class="target-status ${
              agent.reqRetSuper ? "status-ok" : "status-ko"
            }">${
    agent.reqRetSuper
      ? "Target OK"
      : `Mancano ${missingPoliciesSuper} per il Super Bonus`
  }</div>
            <div class="target-val">${agent.retentionRate.toFixed(1)}%</div>
          </div>
        </div>

        ${
          includeAI && aiInsights
            ? `
          <div style="margin-top:30px">
            <div class="section-title">Analisi Business Intelligence (AI)</div>
            <div class="ai-box">${aiInsights}</div>
          </div>
        `
            : ""
        }

        <div class="footer">
          Collaboratore: ${
            agent.name
          } — Generato il: ${new Date().toLocaleString(
    "it-IT",
  )} — Mobility MeritX v3.1
        </div>

        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};
